import { StylesConfig } from "react-select";
import CreatableSelect from "react-select/creatable";

import { useReactSelectTheme } from "@bagel-web/components";

import { parseEnumerationValue } from "../playgroundFunctions";

type EnumerationOption = {
  value: string;
  label: string;
  seeded?: { value: unknown };
};

const enumerationStyles: StylesConfig<EnumerationOption, true> = {
  // The menu is portaled to document.body (see menuPortalTarget below), so
  // it needs a z-index above Bootstrap's overlays to stay visible.
  menuPortal: (baseStyles) => ({
    ...baseStyles,
    zIndex: 1056,
  }),
  multiValueLabel: (baseStyles) => ({
    ...baseStyles,
    fontFamily: "monospace",
  }),
};

// The enumeration ("valid values") editor: a chip per value, new ones typed
// in and parsed as the parameter's type — unparseable text is never offered
// for creation.
const EnumerationField = ({
  id,
  valueType,
  enumeration,
  onChange,
}: {
  id: string;
  valueType: "string" | "integer" | "number";
  enumeration: Array<unknown> | null | undefined;
  onChange: (enumeration: Array<unknown> | null) => void;
}) => {
  const theme = useReactSelectTheme();
  const values = enumeration ?? [];
  const options: Array<EnumerationOption> = values.map((value) => ({
    value: String(value),
    label: String(value),
    seeded: { value },
  }));
  return (
    <div data-testid={`${id}-field`}>
      <CreatableSelect
        inputId={id}
        isMulti
        options={[]}
        noOptionsMessage={() => null}
        // There is no list to drop down, so no arrow suggesting one.
        // This component behaves as an editable set, not a selector.
        components={{ DropdownIndicator: null, IndicatorSeparator: null }}
        value={options}
        placeholder="Any value"
        // Render the menu at the document root so it overlays whatever
        // contains the select (e.g. a Card) instead of scrolling inside it.
        menuPortalTarget={
          typeof document === "undefined" ? null : document.body
        }
        styles={enumerationStyles}
        theme={theme}
        isValidNewOption={(input) => {
          const parsed = parseEnumerationValue(valueType, input);
          return (
            parsed !== null &&
            !options.some((option) => option.value === String(parsed))
          );
        }}
        onChange={(next) => {
          const nextValues: Array<unknown> = [];
          for (const option of next) {
            if (option.seeded) {
              nextValues.push(option.seeded.value);
              continue;
            }
            const parsed = parseEnumerationValue(valueType, option.value);
            if (parsed !== null) {
              nextValues.push(parsed);
            }
          }
          onChange(nextValues.length > 0 ? nextValues : null);
        }}
      />
    </div>
  );
};

export default EnumerationField;
